import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import { Button, Grid2, Typography } from '@riac/design-system';
import RIACLogo from '@riac/core/assets/riac_logo_transparent_black_full.png?h=180';
import RIACGoldLogo from '@riac/core/assets/riac_logo_transparent_gold_full.png?h=180';
import {
  IconFacebook,
  IconTwitter,
  IconYoutube,
  IconBlog,
  IconReddit,
  IconPinterest,
  IconInstagram,
} from '@riac/design-system';

const footerLinkClassName = 'no-underline decoration-2 hover:text-text decoration-accent p-0';

const LinkOnMobileComponent = ({ label, link }: {label: string, link: string}) => {
  const [shouldRenderLink, setShouldRenderLink] = React.useState(false);
  useEffect(() => {
    if((navigator as any)?.userAgentData?.mobile) {
      setShouldRenderLink(true);
    }
  }, []);
  if(shouldRenderLink) return <a href={link}>{label}</a>;
  return label;
};

export const Footer = ():JSX.Element => (
  <footer className='print:hidden bg-surface'>
    <Grid2 className='py-0'>
      <div className='flex flex-col items-center justify-items-between justify-self-stretch w-full'>
        <div className='flex flex-col gap-10 py-10 lg:py-16 w-full'>
          <div className='flex flex-col gap-5 md:flex-row'>
            <div className='gap-8 flex flex-col xl:flex-row shrink-0 flex-1'>
              <div className='gap-8 flex flex-col xl:basis-64'>
                <Button to='/' unstyled className='inline-block'>
                  <img src={RIACGoldLogo} alt='Rock Island Auction Company' className='h-[95px] hidden dark:block'/>
                  <img src={RIACLogo} alt='Rock Island Auction Company' className='h-[95px] block dark:hidden'/>
                </Button>
                <div className='flex flex-col gap-2'>
                  <Typography heading weight='bold'>Contact us</Typography>
                  <div className='flex flex-col gap-1'>
                    <Typography color='text-light'><LinkOnMobileComponent label='1-309-797-1500' link='tel:1-309-797-1500'/></Typography>
                    <Typography color='text-light'><LinkOnMobileComponent label='1-800-238-8022' link='tel:1-800-238-8022'/></Typography>
                    <Typography color='text-light'><a href='mailto:info@rockislandauction.com'>info@rockislandauction.com</a></Typography>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-2'>
                <Typography heading weight='bold'>Locations</Typography>
                <div className='flex flex-col gap-1 items-start'>
                  <Button unstyled to='/location/bedford'>
                    <Typography weight='bold' color='text-light'>Bedford, TX</Typography>
                  </Button>
                  <div>
                    <Typography color='text-light'>3600 Harwood Road</Typography>
                    <Typography color='text-light'>Bedford, Texas 76021, USA</Typography>
                  </div>
                </div>
                <div className='flex flex-col gap-1 items-start'>
                  <Typography weight='bold' color='text-light'>Rock Island, IL</Typography>
                  <div>
                    <Typography color='text-light'>7819 42nd Street West</Typography>
                    <Typography color='text-light'>Rock Island, Illinois 61201, USA</Typography>
                  </div>
                </div>
                <div className='flex flex-col gap-1 items-start'>
                  <Typography weight='bold' color='text-light'>Hours</Typography>
                  <div>
                    <Typography color='text-light'>Monday - Friday</Typography>
                    <Typography color='text-light'>8:00 a.m. - 5:00 p.m.</Typography>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-col xl:flex-row gap-8 justify-start flex-1'>
              <div className='flex flex-col xl:basis-64 gap-2 shrink-0'>
                <Typography heading weight='bold'>
                  Navigation
                </Typography>
                <div className='flex flex-col gap-1 items-start'>
                  <Button to='/gun-auctions/' unstyled className={footerLinkClassName}>
                    <Typography color='text-light'>
                      Auctions
                    </Typography>
                  </Button>
                  <Button to='/consignment/' unstyled className={footerLinkClassName}>
                    <Typography color='text-light'>
                      Consignment
                    </Typography>
                  </Button>
                  <Button to='/news-and-events/' unstyled className={footerLinkClassName}>
                    <Typography color='text-light'>
                      Latest
                    </Typography>
                  </Button>
                  <Button to='/faq/' unstyled className={footerLinkClassName}>
                    <Typography color='text-light'>
                      FAQ
                    </Typography>
                  </Button>
                  <Button to='/about-us/' unstyled className={footerLinkClassName}>
                    <Typography color='text-light'>
                      About Us
                    </Typography>
                  </Button>
                  <Button to='/account/' unstyled className={footerLinkClassName}>
                    <Typography color='text-light'>
                      Account
                    </Typography>
                  </Button>
                </div>
              </div>
              <div className='flex flex-col gap-8 xl:basis-64 shrink-0 justify-between'>
                <div className='flex flex-col gap-2'>
                  <Typography heading weight='bold'>
                    More Info
                  </Typography>
                  <div className='flex flex-col gap-1 items-start'>
                    <Button to='/terms-and-conditions/' unstyled className={footerLinkClassName}>
                      <Typography color='text-light'>
                        Terms & Conditions
                      </Typography>
                    </Button>
                    <Button to='/privacy-policy/' unstyled className={footerLinkClassName}>
                      <Typography color='text-light'>
                        Privacy Policy
                      </Typography>
                    </Button>
                    <Button to='/careers/' unstyled className={footerLinkClassName}>
                      <Typography color='text-light'>
                        Job postings & Careers
                      </Typography>
                    </Button>
                    <Button to='/order-catalog/' unstyled className={footerLinkClassName}>
                      <Typography color='text-light'>
                        Order a catalog
                      </Typography>
                    </Button>
                    <Button to='/contact/' unstyled className={footerLinkClassName}>
                      <Typography color='text-light'>
                        Contact
                      </Typography>
                    </Button>
                  </div>
                </div>
                <div className='gap-2 flex flex-col shrink-0'>
                  <Typography heading weight='bold'>Social media</Typography>
                  <div className='text-text-light flex flex-row gap-2 -ml-1'>
                    <a aria-label='RIAC Blog' className='w-6 h-6 flex items-center justify-center hover:text-text' href='https://www.rockislandauction.com/riac-blog/' target='_blank' rel='noreferrer'><IconBlog/></a>
                    <a aria-label='RIAC YouTube' className='w-6 h-6 flex items-center justify-center hover:text-text' href='https://www.youtube.com/user/RockIslandAuction' target='_blank' rel='noreferrer'><IconYoutube/></a>
                    <a aria-label='RIAC Facebook' className='w-6 h-6 flex items-center justify-center hover:text-text' href='https://www.facebook.com/RockIslandAuctionCompany' target='_blank' rel='noreferrer'><IconFacebook/></a>
                    <a aria-label='RIAC Instagram' className='w-6 h-6 flex items-center justify-center hover:text-text' href='https://www.instagram.com/rockislandauction/' target='_blank' rel='noreferrer'><IconInstagram/></a>
                    <a aria-label='RIAC Twitter' className='w-6 h-6 flex items-center justify-center hover:text-text' href='https://twitter.com/RIauction' target='_blank' rel='noreferrer'><IconTwitter/></a>
                    <a aria-label='RIAC Reddit' className='w-6 h-6 flex items-center justify-center hover:text-text' href='https://www.reddit.com/r/rockislandauction/' target='_blank' rel='noreferrer'><IconReddit/></a>
                    <a aria-label='RIAC Pinterest' className='w-6 h-6 flex items-center justify-center hover:text-text' href='https://www.pinterest.com/riauction/_created/' target='_blank' rel='noreferrer'><IconPinterest/></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-3 xl:pt-0 md:pt-12'>
            <div className='flex flex-row items-center justify-center'>
              <Typography size='sm' color='text-lighter' className='basis-[754px] text-left md:text-center'>
                {/* DayJS: Who cares whose timezone this is*/}
                © {dayjs().format('YYYY')} Rock Island Auction Company. RIAC believes that this website is accessible to the widest possible audience pursuant to the guidelines of the Americans with Disability Act.
                {' '}<Button variant='text' to='/accessibility-statement/' className='text-text-lighter p-0 font-normal'>Click here for more information.</Button>
              </Typography>
            </div>
            <div className='text-left md:text-center'>
              <Button variant='text' to='https://transparency-in-coverage.uhc.com/' className='text-text-lighter p-0 font-normal'>Healthcare Transparency in Coverage.</Button>
            </div>
          </div>
        </div>
      </div>
    </Grid2>
  </footer>
);
